"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { CSSProperties } from "react";
import FactionCard, { FACTIONS } from "./FactionCard";
import { playHover, type FactionKey } from "./sounds";
import { usePhoneLayout } from "@/lib/phoneLayout";
import styles from "../faction.module.css";

export type ParticleType = "feather" | "ember" | "petal" | "leaf" | "ash" | "angelStar" | "dataBit";

type ParticleConfig = {
  count: number;
  minSize: number;
  maxSize: number;
  minDur: number;
  maxDur: number;
  drift: number;
};

// Counts trimmed ~25-35% from the prototype's own JS (feather 16, ember 44,
// petal 12, leaf 14, ash 18, angel-star 12, data-bit 14) — all 5 factions
// animate their particles simultaneously and permanently here (unlike the
// character page, where only the selected character's effects ever run), so
// the same "many concurrently animated elements" performance cost applies
// from the start. Trimmed proactively rather than porting 1:1 and needing a
// second pass; visual density is unaffected at these counts.
const PARTICLE_CONFIG: Record<ParticleType, ParticleConfig> = {
  feather: { count: 12, minSize: 7, maxSize: 15, minDur: 7.5, maxDur: 13, drift: 48 },
  ember: { count: 28, minSize: 3, maxSize: 7, minDur: 2.8, maxDur: 6.2, drift: 42 },
  petal: { count: 10, minSize: 5, maxSize: 9, minDur: 7.5, maxDur: 13, drift: 80 },
  leaf: { count: 11, minSize: 6, maxSize: 12, minDur: 8, maxDur: 14, drift: 95 },
  ash: { count: 14, minSize: 2, maxSize: 4, minDur: 6, maxDur: 11, drift: 36 },
  angelStar: { count: 10, minSize: 2, maxSize: 4, minDur: 4.5, maxDur: 7.5, drift: 16 },
  dataBit: { count: 11, minSize: 8, maxSize: 11, minDur: 5.5, maxDur: 8.5, drift: 0 },
};

const PARTICLE_CLASS: Record<ParticleType, string> = {
  feather: styles.feather,
  ember: styles.ember,
  petal: styles.petal,
  leaf: styles.leaf,
  ash: styles.ash,
  angelStar: styles.angelStar,
  dataBit: styles.dataBit,
};

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}

export type Particle = { key: string; className: string; style: CSSProperties; content?: string };

function buildParticles(type: ParticleType): Particle[] {
  const cfg = PARTICLE_CONFIG[type];
  const particles: Particle[] = [];
  for (let i = 0; i < cfg.count; i++) {
    const style: CSSProperties = {
      ["--x" as string]: `${rand(4, 96).toFixed(2)}%`,
      ["--size" as string]: `${rand(cfg.minSize, cfg.maxSize).toFixed(2)}px`,
      ["--dur" as string]: `${rand(cfg.minDur, cfg.maxDur).toFixed(2)}s`,
      ["--delay" as string]: `${(-rand(0, cfg.maxDur)).toFixed(2)}s`,
      ["--drift" as string]: `${rand(-cfg.drift, cfg.drift).toFixed(1)}px`,
    };
    if (type === "petal") (style as Record<string, string>)["--y"] = `${rand(-12, 22).toFixed(2)}%`;
    particles.push({
      key: `${type}-${i}`,
      className: PARTICLE_CLASS[type],
      style,
      content: type === "dataBit" ? (Math.random() > 0.5 ? "01" : Math.random() > 0.5 ? "10" : "11") : undefined,
    });
  }
  return particles;
}

const ALL_TYPES: ParticleType[] = ["feather", "ember", "petal", "leaf", "ash", "angelStar", "dataBit"];

const KEYS: FactionKey[] = ["angel", "demon", "mortal", "security", "ronin"];

// Phones (owner's pick 2026-10-05): one faction per screen, swipe between
// them; the card in view holds the desktop hover look and the hover blip plays
// as the focus moves; an icon strip at the bottom jumps and tracks.
export default function FactionGrid() {
  // Random, so built client-side only: `mounted` is false on the server and during hydration.
  const mounted = useSyncExternalStore(() => () => {}, () => true, () => false);
  const [particles, setParticles] = useState<Record<ParticleType, Particle[]> | null>(null);
  if (mounted && particles === null) {
    const generated = {} as Record<ParticleType, Particle[]>;
    for (const type of ALL_TYPES) generated[type] = buildParticles(type);
    setParticles(generated);
  }
  const phone = usePhoneLayout();
  const [litKey, setLitKey] = useState<FactionKey | null>(null);
  const litRef = useRef<FactionKey | null>(null);
  const gridRef = useRef<HTMLElement>(null);

  // Phones: whichever card sits nearest the middle of the screen holds the
  // lit state; the hover blip plays once per change of faction (silent until
  // the first tap, per the browsers' autoplay rule).
  useEffect(() => {
    if (!phone) return;
    const el = gridRef.current;
    if (!el) return;
    let raf = 0;
    const pick = () => {
      raf = 0;
      const mid = window.innerWidth / 2;
      let best: FactionKey | null = null, bestD = Infinity;
      for (const c of el.querySelectorAll<HTMLElement>("[data-faction]")) {
        const r = c.getBoundingClientRect();
        const d = Math.abs((r.left + r.right) / 2 - mid);
        if (d < bestD) { bestD = d; best = c.dataset.faction as FactionKey; }
      }
      if (best && best !== litRef.current) { if (litRef.current) playHover(); litRef.current = best; setLitKey(best); }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(pick); };
    raf = requestAnimationFrame(pick);
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { el.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [phone]);

  const jumpTo = (key: FactionKey) => {
    gridRef.current?.querySelector<HTMLElement>(`[data-faction="${key}"]`)?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  return (
    <div className={styles.page}>
      <main ref={gridRef} className={`${styles.factions} ${phone && litKey ? styles.hasLit : ""}`}>
        {KEYS.map((key) => (
          <FactionCard key={key} faction={key} particles={particles} lit={phone && litKey === key} />
        ))}
      </main>
      {phone ? (
        <nav className={styles.deckDots} aria-label="Factions">
          {KEYS.map((key) => (
            <button key={key} type="button" className={`${styles.deckDot} ${FACTIONS[key].className} ${litKey === key ? styles.deckDotOn : ""}`} aria-label={FACTIONS[key].kicker} aria-current={litKey === key || undefined} onClick={() => jumpTo(key)}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={FACTIONS[key].iconSrc} alt="" />
            </button>
          ))}
        </nav>
      ) : null}
    </div>
  );
}
